/**
 * ScrollTheme - Utility to change scrollbar color based on scroll position
 *
 * This script adds CSS classes to the HTML element based on scroll position
 * which triggers different scrollbar colors defined in CSS
 */

// Debounce function to limit how often scroll events are processed
function debounce(func, wait = 10) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Track scroll position and update classes accordingly
function handleScroll() {
  const html = document.documentElement;
  const totalHeight = html.scrollHeight - html.clientHeight;
  const scrollPosition = window.scrollY;

  // Calculate scroll percentage
  const scrollPercentage = (scrollPosition / totalHeight) * 100;

  // Remove all scroll position classes first
  html.classList.remove(
    "scrolled-20",
    "scrolled-40",
    "scrolled-60",
    "scrolled-80"
  );

  // Add appropriate class based on scroll percentage
  if (scrollPercentage >= 80) {
    html.classList.add("scrolled-80");
  } else if (scrollPercentage >= 60) {
    html.classList.add("scrolled-60");
  } else if (scrollPercentage >= 40) {
    html.classList.add("scrolled-40");
  } else if (scrollPercentage >= 20) {
    html.classList.add("scrolled-20");
  }

  // Update CSS variables for color interpolation
  // This allows smooth blending between colors at threshold boundaries
  // Use custom properties to have smooth transitions between breakpoints
  updateScrollbarColorVariable(scrollPercentage);
}

// Function to update the scrollbar color based on exact scroll percentage
function updateScrollbarColorVariable(percentage) {
  const html = document.documentElement;

  // Define our color stops in RGB format for easier interpolation
  const colorStops = [
    { percent: 0, color: [251, 66, 150] }, // --primary-pink at 0%
    { percent: 25, color: [122, 64, 202] }, // --primary-purple at 25%
    { percent: 50, color: [65, 75, 178] }, // --primary-blue at 50%
    { percent: 75, color: [94, 171, 251] }, // --primary-light-blue at 75%
    { percent: 100, color: [17, 151, 162] }, // --primary-teal at 100%
  ];

  // Find the two color stops between which our current percentage falls
  let lowerStop = colorStops[0];
  let upperStop = colorStops[1];

  for (let i = 1; i < colorStops.length; i++) {
    if (percentage <= colorStops[i].percent) {
      upperStop = colorStops[i];
      lowerStop = colorStops[i - 1];
      break;
    }
    if (i === colorStops.length - 1) {
      // If we're at the last stop
      lowerStop = colorStops[i - 1];
      upperStop = colorStops[i];
    }
  }

  // Calculate the percentage between the two stops
  const range = upperStop.percent - lowerStop.percent;
  const adjustedPercentage =
    range === 0 ? 0 : (percentage - lowerStop.percent) / range;

  // Interpolate the color
  const interpolatedColor = lowerStop.color.map((channel, index) => {
    const upperChannel = upperStop.color[index];
    return Math.round(channel + (upperChannel - channel) * adjustedPercentage);
  });

  // Set the custom property
  html.style.setProperty(
    "--scrollbar-thumb-color",
    `rgb(${interpolatedColor[0]}, ${interpolatedColor[1]}, ${interpolatedColor[2]})`
  );
}

// Initialize scroll theme functionality
export function initScrollTheme() {
  // Debounce scroll handler for better performance
  const debouncedHandleScroll = debounce(handleScroll, 10);

  // Add event listener for scroll
  window.addEventListener("scroll", debouncedHandleScroll, { passive: true });

  // Add a style element for the custom scrollbar color rule
  const styleEl = document.createElement("style");
  styleEl.textContent = `
    ::-webkit-scrollbar-thumb {
      background-color: var(--scrollbar-thumb-color, var(--primary-pink)) !important;
    }
    html {
      scrollbar-color: var(--scrollbar-thumb-color, var(--primary-pink)) rgba(255, 255, 255, 0.1) !important;
    }
  `;
  document.head.appendChild(styleEl);

  // Run once on init to set initial state
  handleScroll();

  // Return cleanup function
  return () => {
    window.removeEventListener("scroll", debouncedHandleScroll);
    document.head.removeChild(styleEl);
  };
}

export default initScrollTheme;
